<template>
  <v-card :height="height" :flat="darkMode" :outlined="darkMode" class="item-bottom-border rounded-lg">
    <div @click="show">
      <v-img :height="videoHeight" :src="thumbnailUrl" class="video-preview rounded-t-lg"></v-img>
      <v-container>
        <v-row justify="space-between">
          <v-col v-if="hasGroup">
            <v-chip :class="chipColors" small>{{ groupName }}</v-chip>
          </v-col>
          <v-col v-else>&nbsp;</v-col>
          <v-col class="rel" align="right">
            <v-btn class="btnPos" icon rounded absolute top right @click.stop.prevent="toggleReadState">
              <v-icon :color="isRead ? 'grey' : theme.unreadBadgeColor" class="align-center"
                >{{ isRead ? 'mdi-checkbox-blank-circle-outline' : 'mdi-checkbox-blank-circle' }}
              </v-icon>
            </v-btn>
            <!--<v-badge v-if="isUnread" right top offset-x="20" offset-y="3" :color="theme.unreadBadgeColor"></v-badge>-->
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-card-title class="pa-0 lh-1 ellipsis overflow-hidden">
              {{ video.title }}
            </v-card-title>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col>
            <div class="op-text two-line">{{ video.description }}</div>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col class="d-flex justify-end align-center mt-4">
            <download-dialog v-if="showDownloadBtn" :downloadFileTitle="mx_videoDownloadName(video)" @confirmed="mx_downloadFile(video)"> </download-dialog>
            <v-btn rounded outlined @click.stop.prevent="toggleQuickView">
              <v-icon>mdi-play</v-icon>
              &nbsp;{{ $t('quickRead') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </div>

    <!-- Video Quick View Dialog -->
    <v-dialog v-model="showQuickView" max-width="900" @click:outside="closeQuickView">
      <v-card class="quickView">
        <v-btn color="white closeBtn" fab dark small @click.stop.prevent="closeQuickView">
          <v-icon color="black">mdi-close</v-icon>
        </v-btn>

        <v-container class="py-0 px-0" style="max-width: 900px; margin: 0 auto">
          <v-row dense no-gutters>
            <v-col>
              <!-- Video Player -->
              <div class="video-wrapper">
                <video
                  v-if="videoUrl"
                  ref="videoPlayer"
                  :src="videoUrl"
                  :poster="thumbnailUrl"
                  controls
                  controlsList="nodownload"
                  playsinline
                  autoplay
                  class="video-player"
                  @play="onVideoPlay"
                >
                  Your browser does not support the video tag.
                </video>
                <div v-else class="pa-4 text-center">
                  <v-icon large>mdi-video-off</v-icon>
                  <div class="mt-2">Video not available</div>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-container>
        <v-container class="py-4 px-6" style="max-width: 800px; margin: 0 auto">
          <v-row dense no-gutters>
            <v-col cols="12" class="mb-2">
              <v-chip v-if="hasGroup" :class="chipColors" small>{{ groupName }}</v-chip>
            </v-col>
            <v-col cols="12" class="mb-2">
              <subtitle-2
                :item="video"
                :displayPublicationTime="displayPublicationTime"
                :display-view-counter="moduleConfig.displayViewCounter"
                :downloadBtn="showDownloadBtn"
                :downloadFileTitle="mx_videoDownloadName(video)"
                @onDownload="mx_downloadFile(video)"
              ></subtitle-2>
            </v-col>
            <v-col cols="12" class="mb-2">
              <div class="text-h5 font-weight-medium">{{ video.title }}</div>
            </v-col>

            <v-col cols="12">
              <div v-if="video.description" class="text-body-1">
                {{ video.description }}
              </div>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import dateTimeMixin from '@/mixins/dateTimeMixin.js';
import common from '@/mixins/common';
import configMixin from '@/mixins/configMixin';
import videoMixin from './videoMixin';
import readService from '@/services/readService';
import DownloadDialog from '@/dialogs/DownloadDialog';
import Subtitle2 from '../../components/Subtitle2';

export default {
  mixins: [common, dateTimeMixin, configMixin, videoMixin],
  props: {
    video: Object,
    height: Number,
    videoHeight: { type: Number, default: 200 },
    objectFit: { type: String, default: 'cover' }, //cover or contain
  },
  components: {
    DownloadDialog,
    Subtitle2,
  },
  data() {
    return {
      isRead: readService.isRead(this.video),
      showQuickView: false,
    };
  },
  computed: {
    displayPublicationTime() {
      if (this.moduleConfig) {
        return this.moduleConfig.displayPublicationTime !== undefined ? this.moduleConfig.displayPublicationTime : true;
      }
      return true;
    },
    moduleConfig() {
      let moduleConfig = this.getModuleConfig('Video', this.$route.params.instance);
      return moduleConfig;
    },
    thumbnailUrl() {
      return this.mx_thumbnailUrl(this.video);
    },
    videoUrl() {
      return this.mx_videoUrl(this.video);
    },
    hasGroup() {
      return this.video._group ? true : false;
    },
    groupName() {
      return this.hasGroup && this.video._group.name ? this.video._group.name : '';
    },
    isUnread() {
      return !readService.isRead(this.video);
    },
    videoDownloadName() {
      return this.mx_videoDownloadName(this.video);
    },
    showDownloadBtn() {
      if (this.video.downloadable !== undefined) {
        return Boolean(this.video.downloadable);
      }

      return true;
    },
  },
  methods: {
    show() {
      this.$router.push(`/videos/${this.video.instance}/${this.video.id}`);
    },
    toggleReadState() {
      if (this.video) {
        const isRead = this.isRead;

        let type = this.$t(`type.${this.video._moduleId}`); //e.g. type.video or type.news
        let state = this.$t(`${isRead ? 'unseen' : 'seen'}.${this.video._moduleId}`);
        let actionName = this.$t('action.marked');
        let sendboxTitle = this.video.title;
        let snackbarText = this.$t('markAsMsg', { type, title: this.video.title, state, actionName });

        let action = isRead ? 'markAsUnread2' : 'markAsRead2';

        this.$store.dispatch(
          `sendbox/${action}`,
          {
            item: this.video,
            sendboxTitle,
            snackbarText,
            immediate: true,
          },
          { root: true }
        );
        this.isRead = !isRead;
      }
    },
    toggleQuickView() {
      this.showQuickView = !this.showQuickView;
      if (this.showQuickView) {
        if (this.isUnread) {
          this.markAsRead();
        }
        // Play video after dialog opens
        this.$nextTick(() => {
          if (this.$refs.videoPlayer) {
            this.$refs.videoPlayer.play().catch((err) => {
              console.log('Autoplay prevented:', err);
            });
          }
        });
      }
    },
    closeQuickView() {
      this.showQuickView = false;
      // Pause video when closing dialog
      if (this.$refs.videoPlayer) {
        this.$refs.videoPlayer.pause();
      }
    },
    onVideoPlay() {
      // Mark as read when video starts playing
      if (this.isUnread) {
        this.markAsRead();
      }
    },
    markAsRead() {
      if (this.video) {
        let type = this.$t(`type.${this.video._moduleId}`);
        let state = this.$t(`seen.${this.video._moduleId}`);
        let actionName = this.$t('action.marked');
        let sendboxTitle = this.video.title;
        let snackbarText = this.$t('markAsMsg', { type, title: this.video.title, state, actionName });

        this.$store.dispatch(
          `sendbox/markAsRead2`,
          {
            item: this.video,
            sendboxTitle,
            snackbarText,
            immediate: true,
          },
          { root: true }
        );
        this.isRead = true;
      }
    },
  },
  beforeDestroy() {
    // Clean up video player when component is destroyed
    if (this.$refs.videoPlayer) {
      this.$refs.videoPlayer.pause();
      this.$refs.videoPlayer.src = '';
    }
  },
};
</script>

<style scoped>
.rel {
  position: relative;
}
.btnPos {
  top: 7px !important;
  right: 7px !important;
}
.lh-1 {
  line-height: 1;
}
.closeBtn {
  width: 32px !important;
  height: 32px !important;
  opacity: 0.8;
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 10;
}

.quickView {
  border: 3px solid white;
}

.video-wrapper {
  width: 100%;
  background: #000;
  border-radius: 8px;
  overflow: hidden;
}

.video-player {
  width: 100%;
  height: auto;
  max-height: 70vh;
  display: block;
}

.theme--dark .quickView {
  background: #202020;
  border: 3px solid #404040;
}

.theme--dark .video-wrapper {
  background: #1a1a1a;
}
</style>
